import React, { useState, useEffect } from 'react';

const BarkerProducciones = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home'); // 'home' o 'cart'
  const [cart, setCart] = useState(() => {
    // Cargar carrito desde localStorage
    const savedCart = localStorage.getItem('barkerCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('barkerCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (service) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === service.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === service.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...service, quantity: 1 }];
    });
  };

  const removeFromCart = (serviceId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== serviceId));
  };

  const updateQuantity = (serviceId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(serviceId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === serviceId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="barker-app">
      <Header 
        isMobileMenuOpen={isMobileMenuOpen} 
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        cartCount={cartCount}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      
      {currentPage === 'home' ? (
        <>
          <HeroSection scrollY={scrollY} />
          <ServicesSection addToCart={addToCart} />
          <GallerySection />
          <ContactFormSection />
          <LocationMapSection />
          <Footer setCurrentPage={setCurrentPage} />
        </>
      ) : (
        <CartPage 
          cart={cart}
          removeFromCart={removeFromCart}
          updateQuantity={updateQuantity}
          clearCart={clearCart}
          setCurrentPage={setCurrentPage}
        />
      )}
      
      <ChatWidget />
      <style jsx>{styles}</style>
    </div>
  );
};

const Header = ({ isMobileMenuOpen, setIsMobileMenuOpen, cartCount, currentPage, setCurrentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div 
        className="logo"
        onClick={() => setCurrentPage('home')}
        style={{ cursor: 'pointer' }}
      >
        <div className="logo-circle">B</div>
        <div className="logo-text">Barker Producciones</div>
      </div>
      
      <div className="header-right">
        <div className="social-icons">
          <a 
            href="https://www.facebook.com/barkerproducciones" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon facebook"
            aria-label="Facebook"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a 
            href="https://www.instagram.com/barkerproducciones" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-icon instagram"
            aria-label="Instagram"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
        </div>

        {/* Cart Icon */}
        <div 
          className="cart-icon-container"
          onClick={() => setCurrentPage('cart')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          {cartCount > 0 && (
            <span className="cart-badge">{cartCount}</span>
          )}
        </div>
        
        <button 
          className="btn-llamanos"
          onClick={() => alert('Llámanos al: +56 9 6579 0743')}
        >
          Llámanos
        </button>
      </div>
    </header>
  );
};

const HeroSection = ({ scrollY }) => {
  const parallaxStyle = {
    transform: `translateY(${scrollY * 0.5}px)`
  };

  return (
    <section className="hero">
      <div className="hero-background" style={parallaxStyle}></div>
      <div className="hero-overlay"></div>
      
      <div className="hero-content">
        <h1>
          "Transformamos eventos en experiencias<br />
          que <strong>emocionan</strong>."
        </h1>
        <p className="hero-subtitle">SOMOS PRODUCCIÓN EN CHILE.</p>
        <button 
          className="btn-cotizar"
          onClick={() => alert('¡Cotiza tu evento! Contáctanos para más información.')}
        >
          Cotizar Evento
        </button>
      </div>

      <div className="wave">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
        </svg>
      </div>
    </section>
  );
};

const ChatWidget = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`chat-widget ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => alert('Chat en línea - ¡Contáctanos ahora!')}
    >
      <span className="chat-icon">💬</span>
      <span className="chat-text">Estoy en línea</span>
    </div>
  );
};

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  // Galería de imágenes de ejemplo con placeholders
  const images = [
    { id: 1, category: 'corporativos', title: 'Evento Corporativo Premium', image: 'https://i.ibb.co/vCfML9xk/mesa.jpg' },
    { id: 2, category: 'bodas', title: 'Boda Elegante', image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80' },
    { id: 3, category: 'corporativos', title: 'Conferencia Internacional', image: 'https://i.ibb.co/Pv1h72hc/Whats-App-Image-2026-01-08-at-17-23-31-6.jpg' },
    { id: 4, category: 'shows', title: 'Show en Vivo', image: 'https://i.ibb.co/DfcvFhwR/Captura-de-pantalla-2026-01-08-a-la-s-20-10-53.png' },
    { id: 5, category: 'bodas', title: 'Recepción de Ensueño', image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&q=80' },
    { id: 6, category: 'corporativos', title: 'Lanzamiento de Producto', image: 'https://i.ibb.co/JjkRm0FK/Whats-App-Image-2026-01-08-at-17-29-14-2.jpg' },
    { id: 7, category: 'shows', title: 'Concierto Masivo', image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&q=80' },
    { id: 8, category: 'corporativos', title: 'Seminario Empresarial', image: 'https://i.ibb.co/d49xKD6B/Whats-App-Image-2026-01-08-at-17-29-39.jpg' },
    { id: 9, category: 'bodas', title: 'Ceremonia al Aire Libre', image: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&q=80' },
    { id: 10, category: 'shows', title: 'Festival de Música', image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80' },
    { id: 11, category: 'corporativos', title: 'Gala Anual', image: 'https://images.unsplash.com/photo-1478147427282-58a87a120781?w=800&q=80' },
    { id: 12, category: 'bodas', title: 'Celebración Íntima', image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&q=80' }
  ];

  const categories = [
    { id: 'all', name: 'Todos' },
    { id: 'corporativos', name: 'Corporativos' },
    { id: 'bodas', name: 'Bodas' },
    { id: 'shows', name: 'Shows' }
  ];

  const filteredImages = filter === 'all' 
    ? images 
    : images.filter(img => img.category === filter);

  return (
    <>
      <section className="gallery-section">
        <div className="gallery-container">
          <h2 className="gallery-title">NUESTRA GALERÍA</h2>
          <p className="gallery-subtitle">Momentos inolvidables capturados en cada evento</p>
          
          {/* Filtros */}
          <div className="gallery-filters">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`filter-btn ${filter === cat.id ? 'active' : ''}`}
                onClick={() => setFilter(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Grid de imágenes */}
          <div className="gallery-grid">
            {filteredImages.map((image) => (
              <div 
                key={image.id} 
                className="gallery-item"
                onClick={() => setSelectedImage(image)}
              >
                <img src={image.image} alt={image.title} loading="lazy" />
                <div className="gallery-overlay">
                  <h3>{image.title}</h3>
                  <span className="zoom-icon">🔍</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <button className="lightbox-close" onClick={() => setSelectedImage(null)}>✕</button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.image} alt={selectedImage.title} />
            <h3>{selectedImage.title}</h3>
          </div>
        </div>
      )}
    </>
  );
};

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: '8c8b4e8d-6b7f-4c9a-9d8e-3f2a1b4c5d6e', // Reemplazar con tu key real
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          from_name: 'Barker Producciones Website',
          to_email: 'contacto@barkerproducciones.cl'
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        // Limpiar mensaje de éxito después de 5 segundos
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form-section">
      <div className="contact-container">
        <h2 className="contact-title">CONTÁCTANOS</h2>
        <p className="contact-subtitle">
          Completa el formulario y nos pondremos en contacto contigo a la brevedad
        </p>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nombre Completo *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Tu nombre"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="tu@email.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Teléfono *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+56 9 XXXX XXXX"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Tipo de Evento *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona una opción</option>
                <option value="Evento Corporativo">Evento Corporativo</option>
                <option value="Boda">Boda</option>
                <option value="Show/Festival">Show/Festival</option>
                <option value="Evento Municipal">Evento Municipal</option>
                <option value="Feria">Feria</option>
                <option value="Congreso/Seminario">Congreso/Seminario</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">Mensaje *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Cuéntanos sobre tu evento..."
              rows="5"
            ></textarea>
          </div>

          <button 
            type="submit" 
            className="contact-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
          </button>

          {submitStatus === 'success' && (
            <div className="form-message success">
              ✓ ¡Mensaje enviado con éxito! Te contactaremos pronto.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="form-message error">
              ✗ Error al enviar el mensaje. Por favor, inténtalo nuevamente o contáctanos por WhatsApp.
            </div>
          )}
        </form>

        <div className="contact-info-boxes">
          <div className="info-box">
            <div className="info-icon">📞</div>
            <h4>Teléfono</h4>
            <p>+56 9 6579 0743</p>
          </div>
          <div className="info-box">
            <div className="info-icon">📧</div>
            <h4>Email</h4>
            <p>contacto@barkerproducciones.cl</p>
          </div>
          <div className="info-box">
            <div className="info-icon">📍</div>
            <h4>Ubicación</h4>
            <p>Chillán, Chile</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const LocationMapSection = () => {
  return (
    <section className="location-section">
      <div className="location-container">
        <h2 className="location-title">NUESTRA UBICACIÓN</h2>
        <p className="location-subtitle">Encuéntranos en Chillán, Chile</p>
        
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d50776.89203476634!2d-72.13659992089843!3d-36.60644699999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9669b61b6b964c5f%3A0x4c0c49c6c1a3b3f4!2sChillán%2C%20Chile!5e0!3m2!1ses!2scl!4v1640000000000!5m2!1ses!2scl"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación Barker Producciones - Chillán, Chile"
          ></iframe>
        </div>

        <div className="location-details">
          <div className="location-detail-item">
            <strong>📍 Dirección:</strong>
            <p>Chillán, Región de Ñuble, Chile</p>
          </div>
          <div className="location-detail-item">
            <strong>🕐 Horario de Atención:</strong>
            <p>Lunes a Viernes: 9:00 - 18:00 hrs</p>
            <p>Sábados: 10:00 - 14:00 hrs</p>
          </div>
          <div className="location-detail-item">
            <strong>💬 Atención Inmediata:</strong>
            <p>
              <a href="https://wa.me/56965790743" target="_blank" rel="noopener noreferrer" className="whatsapp-link">
                Contáctanos por WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const CartPage = ({ cart, removeFromCart, updateQuantity, clearCart, setCurrentPage }) => {
  const [checkoutData, setCheckoutData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventDate: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const calculateTotal = () => {
    // Aquí puedes agregar precios si los tienes
    // Por ahora solo retornamos la cantidad de servicios
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckoutChange = (e) => {
    setCheckoutData({
      ...checkoutData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Crear mensaje para WhatsApp
    const cartItems = cart.map(item => 
      `• ${item.title} ${item.subtitle} (Cantidad: ${item.quantity})`
    ).join('%0A');

    const message = `¡Nuevo pedido!%0A%0A` +
      `Nombre: ${checkoutData.name}%0A` +
      `Email: ${checkoutData.email}%0A` +
      `Teléfono: ${checkoutData.phone}%0A` +
      `Empresa: ${checkoutData.company}%0A` +
      `Fecha del Evento: ${checkoutData.eventDate}%0A%0A` +
      `Servicios solicitados:%0A${cartItems}%0A%0A` +
      `Notas adicionales: ${checkoutData.notes || 'Ninguna'}`;

    // Abrir WhatsApp con el mensaje
    window.open(`https://wa.me/56965790743?text=${message}`, '_blank');

    // Limpiar carrito después de 2 segundos
    setTimeout(() => {
      clearCart();
      setSubmitStatus('success');
      setIsSubmitting(false);
    }, 2000);
  };

  useEffect(() => {
    // Scroll to top when cart page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <button className="back-btn" onClick={() => setCurrentPage('home')}>
            ← Volver a Servicios
          </button>
          <h1 className="cart-title">TU CARRITO</h1>
          <p className="cart-subtitle">Revisa los servicios seleccionados</p>
        </div>

        {cart.length === 0 ? (
          <div className="cart-empty">
            <div className="empty-icon">🛒</div>
            <h2>Tu carrito está vacío</h2>
            <p>Agrega algunos servicios para comenzar</p>
            <button className="btn-continue" onClick={() => setCurrentPage('home')}>
              Ver Servicios
            </button>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items">
              <div className="cart-items-header">
                <h3>Servicios Seleccionados ({cart.length})</h3>
                {cart.length > 0 && (
                  <button className="btn-clear" onClick={clearCart}>
                    🗑️ Vaciar Carrito
                  </button>
                )}
              </div>

              {cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-info">
                    <h4>{item.title} {item.subtitle}</h4>
                    <p>{item.description}</p>
                  </div>
                  
                  <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button 
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        −
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button 
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button 
                      className="btn-remove"
                      onClick={() => removeFromCart(item.id)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-checkout">
              <div className="cart-summary">
                <h3>Resumen</h3>
                <div className="summary-line">
                  <span>Total de servicios:</span>
                  <strong>{calculateTotal()}</strong>
                </div>
                <p className="summary-note">
                  El precio final será cotizado según tus necesidades específicas
                </p>
              </div>

              <form className="checkout-form" onSubmit={handleCheckout}>
                <h3>Información de Contacto</h3>
                
                <div className="form-group">
                  <label htmlFor="name">Nombre Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={checkoutData.name}
                    onChange={handleCheckoutChange}
                    required
                    placeholder="Tu nombre"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={checkoutData.email}
                    onChange={handleCheckoutChange}
                    required
                    placeholder="tu@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Teléfono *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={checkoutData.phone}
                    onChange={handleCheckoutChange}
                    required
                    placeholder="+56 9 XXXX XXXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Empresa</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={checkoutData.company}
                    onChange={handleCheckoutChange}
                    placeholder="Nombre de tu empresa (opcional)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="eventDate">Fecha del Evento *</label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={checkoutData.eventDate}
                    onChange={handleCheckoutChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notas Adicionales</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={checkoutData.notes}
                    onChange={handleCheckoutChange}
                    rows="4"
                    placeholder="Cuéntanos más sobre tu evento..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn-checkout"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Procesando...' : '💬 Enviar Cotización por WhatsApp'}
                </button>

                {submitStatus === 'success' && (
                  <div className="checkout-success">
                    ✓ ¡Pedido enviado! Te contactaremos pronto.
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Footer = ({ setCurrentPage }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section">
          <div className="footer-logo">
            <div className="footer-logo-circle">B</div>
            <h3>Barker Producciones</h3>
          </div>
          <p className="footer-description">
            Transformamos eventos en experiencias que emocionan.
          </p>
        </div>

        <div className="footer-section">
          <h4>Contacto</h4>
          <ul className="footer-links">
            <li>📞 +56 9 6579 0743</li>
            <li>📧 contacto@barkerproducciones.cl</li>
            <li>📍 Santiago, Chile</li>
          </ul>
        </div>

        <div className="footer-section">
          <h4>Síguenos</h4>
          <div className="footer-social">
            <a 
              href="https://www.facebook.com/barkerproducciones" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-social-icon facebook"
              aria-label="Facebook"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/barkerproducciones" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-social-icon instagram"
              aria-label="Instagram"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://wa.me/56965790743" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-social-icon whatsapp"
              aria-label="WhatsApp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="footer-sercotec">
        <div className="sercotec-container">
          <p className="sercotec-text">Proyecto financiado por:</p>
          <div className="sercotec-logo">
            <img 
              src="https://i.ibb.co/B5B9rJs4/sercoteclogo.png" 
              alt="SERCOTEC - Servicio de Cooperación Técnica"
              loading="lazy"
            />
          </div>
          <p className="sercotec-disclaimer">
            Este sitio web ha sido desarrollado con el apoyo de SERCOTEC, 
            Servicio de Cooperación Técnica del Ministerio de Economía, Fomento y Turismo.
          </p>
        </div>
      </div>

      <div className="footer-bottom">
        <p>&copy; {currentYear} Barker Producciones. Todos los derechos reservados.</p>
        <p className="footer-tagline">Somos producción en Chile</p>
      </div>
    </footer>
  );
};


const ServicesSection = ({ addToCart }) => {
  const services = [
    { 
      id: 1, 
      title: 'Lanzamientos', 
      subtitle: 'MARCAS',
      image: 'show',
      description: 'Eventos espectaculares para lanzamientos de productos y marcas'
    },
    { 
      id: 2, 
      title: 'Eventos', 
      subtitle: 'CORPORATIVOS',
      image: 'corporate',
      description: 'Soluciones integrales para eventos empresariales'
    },
    { 
      id: 3, 
      title: 'Producción', 
      subtitle: 'PREMIOS',
      image: 'awards',
      description: 'Ceremonias de premiación memorables'
    },
    { 
      id: 4, 
      title: 'Eventos', 
      subtitle: 'MUNICIPALES',
      image: 'municipal',
      description: 'Grandes eventos para comunidades'
    },
    { 
      id: 5, 
      title: 'Producción', 
      subtitle: 'CATERING',
      image: 'catering',
      description: 'Servicio gastronómico de primer nivel'
    },
    { 
      id: 6, 
      title: 'Escenarios', 
      subtitle: 'EXCLUSIVOS',
      image: 'stage',
      description: 'Diseños únicos y espectaculares'
    },
    { 
      id: 7, 
      title: 'Stands', 
      subtitle: 'CORPORATIVOS',
      image: 'booth',
      description: 'Espacios de marca impactantes'
    },
    { 
      id: 8, 
      title: 'Ferias', 
      subtitle: 'TEMÁTICAS',
      image: 'fair',
      description: 'Organización completa de ferias'
    },
    { 
      id: 9, 
      title: 'Producción', 
      subtitle: 'FESTIVALES',
      image: 'festival',
      description: 'Experiencias musicales y culturales'
    },
    { 
      id: 10, 
      title: 'Congresos', 
      subtitle: 'SEMINARIOS',
      image: 'congress',
      description: 'Eventos académicos y profesionales'
    },
    { 
      id: 11, 
      title: 'Fiestas', 
      subtitle: 'TEMÁTICAS',
      image: 'party',
      description: 'Celebraciones personalizadas'
    },
    { 
      id: 12, 
      title: 'Aniversarios', 
      subtitle: '',
      image: 'anniversary',
      description: 'Momentos especiales inolvidables'
    },
    { 
      id: 13, 
      title: 'Eventos', 
      subtitle: 'INFANTILES',
      image: 'kids',
      description: 'Diversión garantizada para los más pequeños'
    },
    { 
      id: 14, 
      title: 'Eventos', 
      subtitle: 'DEPORTIVOS',
      image: 'sports',
      description: 'Competencias y torneos profesionales'
    },
    { 
      id: 15, 
      title: 'Eventos', 
      subtitle: 'RELIGIOSOS',
      image: 'religious',
      description: 'Ceremonias con respeto y dedicación'
    },
    { 
      id: 16, 
      title: 'Graduaciones', 
      subtitle: 'TITULACIONES',
      image: 'graduation',
      description: 'Celebra tus logros académicos'
    },
    { 
      id: 17, 
      title: 'Producción', 
      subtitle: 'SHOWS',
      image: 'shows',
      description: 'Espectáculos de alto impacto'
    },
    { 
      id: 18, 
      title: 'Red', 
      subtitle: 'CARPET',
      image: 'redcarpet',
      description: 'Eventos VIP con estilo'
    },
    { 
      id: 19, 
      title: 'Fiestas', 
      subtitle: 'MASIVAS',
      image: 'massive',
      description: 'Grandes celebraciones multitudinarias'
    },
    { 
      id: 20, 
      title: 'Espectáculos', 
      subtitle: 'BALLET',
      image: 'ballet',
      description: 'Arte y cultura en escena'
    },
    { 
      id: 21, 
      title: 'Shows', 
      subtitle: 'EN VIVO',
      image: 'live',
      description: 'Experiencias musicales únicas'
    },
    { 
      id: 22, 
      title: 'Diseños', 
      subtitle: 'ESCENOGRÁFICOS',
      image: 'scenography',
      description: 'Creatividad sin límites'
    }
  ];

  const getBackgroundGradient = (image) => {
    const gradients = {
      show: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      corporate: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      awards: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      municipal: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      catering: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      stage: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      booth: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      fair: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      festival: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      congress: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
      party: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
      anniversary: 'linear-gradient(135deg, #f8b500 0%, #fceabb 100%)',
      kids: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      sports: 'linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)',
      religious: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      graduation: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
      shows: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      redcarpet: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
      massive: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      ballet: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      live: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      scenography: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    };
    return gradients[image] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  };

  return (
    <section className="services-section">
      <div className="services-container">
        <h2 className="services-title">EVENTOS PARA EMPRESAS</h2>
        
        <div className="services-grid">
          {services.map((service) => (
            <div key={service.id} className="service-card">
              <div 
                className="service-image"
                style={{ background: getBackgroundGradient(service.image) }}
              >
                <div className="service-overlay"></div>
                <div className="service-content">
                  <h3 className="service-main-title">{service.title}</h3>
                  {service.subtitle && (
                    <p className="service-subtitle">{service.subtitle}</p>
                  )}
                </div>
              </div>
              <div className="service-buttons">
                <button 
                  className="service-btn service-btn-cart"
                  onClick={() => {
                    addToCart(service);
                    alert('¡Servicio agregado al carrito!');
                  }}
                >
                  🛒 AGREGAR AL CARRITO
                </button>
                <button 
                  className="service-btn service-btn-whatsapp"
                  onClick={() => window.open('https://wa.me/56965790743?text=Hola, quiero cotizar ' + service.title + ' ' + service.subtitle, '_blank')}
                >
                  💬 COTIZAR POR WHATSAPP
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .barker-app {
    font-family: 'Arial', sans-serif;
    overflow-x: hidden;
  }

  /* Header */
  .header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 60px;
    background: rgba(255, 255, 255, 0.98);
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
  }

  .header.scrolled {
    padding: 15px 60px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 15px;
    cursor: pointer;
  }

  .logo-circle {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: bold;
    color: white;
    transition: transform 0.3s ease;
  }

  .logo:hover .logo-circle {
    transform: rotate(360deg);
  }

  .logo-text {
    font-size: 24px;
    font-weight: bold;
    color: #2c3e50;
  }

  .btn-llamanos {
    background: #e74c3c;
    color: white;
    padding: 15px 40px;
    border: none;
    border-radius: 30px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: all 0.3s ease;
  }

  .btn-llamanos:hover {
    background: #c0392b;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(231, 76, 60, 0.4);
  }

  .btn-llamanos:active {
    transform: translateY(0);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 30px;
  }

  .social-icons {
    display: flex;
    gap: 15px;
    align-items: center;
  }

  .social-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    text-decoration: none;
  }

  .social-icon.facebook {
    background: #1877f2;
    color: white;
  }

  .social-icon.facebook:hover {
    background: #0d65d9;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(24, 119, 242, 0.4);
  }

  .social-icon.instagram {
    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    color: white;
  }

  .social-icon.instagram:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(188, 24, 136, 0.4);
  }

  /* Cart Icon */
  .cart-icon-container {
    position: relative;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f39c12;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: white;
  }

  .cart-icon-container:hover {
    background: #e67e22;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(243, 156, 18, 0.4);
  }

  .cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #e74c3c;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
    border: 2px solid white;
  }

  /* Hero Section */
  .hero {
    margin-top: 90px;
    height: calc(100vh - 90px);
    min-height: 600px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 60px;
    position: relative;
    overflow: hidden;
  }

  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #2c3e50 0%, #1a252f 100%);
    z-index: 1;
  }

  .hero-background::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(243, 156, 18, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 50%, rgba(230, 126, 34, 0.12) 0%, transparent 45%),
      radial-gradient(circle at 50% 80%, rgba(231, 76, 60, 0.1) 0%, transparent 50%);
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  .hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .hero-content {
    max-width: 900px;
    color: white;
    z-index: 3;
    position: relative;
    animation: fadeInUp 1s ease;
    text-align: left;
    padding-bottom: 100px;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-content h1 {
    font-size: 62px;
    line-height: 1.2;
    margin-bottom: 30px;
    font-weight: 300;
    text-shadow: 2px 2px 10px rgba(0, 0, 0, 0.3);
  }

  .hero-content h1 strong {
    font-weight: 700;
    color: #f39c12;
  }

  .hero-subtitle {
    font-size: 20px;
    letter-spacing: 4px;
    margin-bottom: 50px;
    font-weight: 400;
    opacity: 0.95;
  }

  .btn-cotizar {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;
    padding: 20px 60px;
    border: none;
    border-radius: 50px;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 2px;
    transition: all 0.4s ease;
    display: inline-block;
    box-shadow: 0 10px 30px rgba(231, 76, 60, 0.3);
  }

  .btn-cotizar:hover {
    background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(231, 76, 60, 0.5);
  }

  .btn-cotizar:active {
    transform: translateY(-1px) scale(0.98);
  }

  /* Wave decoration */
  .wave {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    overflow: hidden;
    line-height: 0;
    z-index: 4;
    pointer-events: none;
  }

  .wave svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
    height: 150px;
    transform: translateY(1px);
  }

  .wave .shape-fill {
    fill: #FFFFFF;
  }

  /* Footer */
  .footer {
    background: linear-gradient(135deg, #2c3e50 0%, #1a252f 100%);
    color: white;
    padding: 60px 60px 30px;
  }

  .footer-content {
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 50px;
    margin-bottom: 40px;
  }

  .footer-section h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: #f39c12;
  }

  .footer-section h4 {
    font-size: 18px;
    margin-bottom: 20px;
    color: #f39c12;
    font-weight: bold;
  }

  .footer-logo {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 20px;
  }

  .footer-logo-circle {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: bold;
    color: white;
  }

  .footer-description {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    font-size: 16px;
  }

  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .footer-links li {
    margin-bottom: 12px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 15px;
  }

  .footer-social {
    display: flex;
    gap: 15px;
  }

  .footer-social-icon {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    text-decoration: none;
  }

  .footer-social-icon.facebook {
    background: #1877f2;
    color: white;
  }

  .footer-social-icon.facebook:hover {
    background: #0d65d9;
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(24, 119, 242, 0.5);
  }

  .footer-social-icon.instagram {
    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    color: white;
  }

  .footer-social-icon.instagram:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(188, 24, 136, 0.5);
  }

  .footer-social-icon.whatsapp {
    background: #25D366;
    color: white;
  }

  .footer-social-icon.whatsapp:hover {
    background: #1ebe57;
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(37, 211, 102, 0.5);
  }

  .footer-bottom {
    max-width: 1400px;
    margin: 0 auto;
    padding-top: 30px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
  }

  .footer-bottom p {
    margin: 10px 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
  }

  .footer-tagline {
    color: #f39c12 !important;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  /* SERCOTEC Section in Footer */
  .footer-sercotec {
    background: rgba(255, 255, 255, 0.05);
    padding: 40px 60px;
    margin-top: 40px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .sercotec-container {
    max-width: 1400px;
    margin: 0 auto;
    text-align: center;
  }

  .sercotec-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .sercotec-logo {
    margin: 20px 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sercotec-logo img {
    max-width: 200px;
    height: auto;
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }

  .sercotec-disclaimer {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    max-width: 600px;
    margin: 20px auto 0;
    line-height: 1.6;
  }

  /* Contact Form Section */
  .contact-form-section {
    padding: 100px 60px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }

  .contact-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .contact-title {
    text-align: center;
    font-size: 48px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 20px;
    letter-spacing: 2px;
  }

  .contact-subtitle {
    text-align: center;
    font-size: 18px;
    color: #7f8c8d;
    margin-bottom: 50px;
  }

  .contact-form {
    background: white;
    padding: 50px;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    margin-bottom: 50px;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
    margin-bottom: 30px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-group label {
    font-size: 14px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 15px 20px;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    font-size: 16px;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #e74c3c;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 120px;
  }

  .contact-submit-btn {
    width: 100%;
    padding: 20px;
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 18px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
  }

  .contact-submit-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(231, 76, 60, 0.3);
  }

  .contact-submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .form-message {
    margin-top: 20px;
    padding: 15px;
    border-radius: 10px;
    text-align: center;
    font-weight: bold;
    animation: fadeIn 0.3s ease;
  }

  .form-message.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .form-message.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .contact-info-boxes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
  }

  .info-box {
    background: white;
    padding: 40px 30px;
    border-radius: 15px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .info-box:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .info-icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  .info-box h4 {
    font-size: 18px;
    color: #2c3e50;
    margin-bottom: 10px;
    font-weight: bold;
  }

  .info-box p {
    color: #7f8c8d;
    font-size: 16px;
    margin: 0;
  }

  /* Location Map Section */
  .location-section {
    padding: 100px 60px;
    background: white;
  }

  .location-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .location-title {
    text-align: center;
    font-size: 48px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 20px;
    letter-spacing: 2px;
  }

  .location-subtitle {
    text-align: center;
    font-size: 18px;
    color: #7f8c8d;
    margin-bottom: 50px;
  }

  .map-container {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    margin-bottom: 50px;
  }

  .map-container iframe {
    display: block;
    width: 100%;
  }

  .location-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
  }

  .location-detail-item {
    background: #f8f9fa;
    padding: 30px;
    border-radius: 15px;
    border-left: 5px solid #e74c3c;
  }

  .location-detail-item strong {
    display: block;
    color: #2c3e50;
    font-size: 16px;
    margin-bottom: 15px;
  }

  .location-detail-item p {
    color: #7f8c8d;
    margin: 5px 0;
    line-height: 1.6;
  }

  .whatsapp-link {
    color: #25D366;
    font-weight: bold;
    text-decoration: none;
    transition: color 0.3s ease;
  }

  .whatsapp-link:hover {
    color: #1ebe57;
    text-decoration: underline;
  }

  /* Chat widget */
  .chat-widget {
    position: fixed;
    bottom: 30px;
    right: 30px;
    background: #e74c3c;
    color: white;
    padding: 15px 25px;
    border-radius: 30px;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    box-shadow: 0 5px 20px rgba(231, 76, 60, 0.4);
    z-index: 999;
    transition: all 0.3s ease;
  }

  .chat-widget:hover,
  .chat-widget.hovered {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(231, 76, 60, 0.5);
  }

  .chat-widget:active {
    transform: translateY(-1px);
  }

  .chat-icon {
    font-size: 20px;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }

  .chat-text {
    font-weight: bold;
    font-size: 14px;
  }

  /* Gallery Section */
  .gallery-section {
    padding: 100px 60px;
    background: #ffffff;
  }

  .gallery-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .gallery-title {
    text-align: center;
    font-size: 48px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 20px;
    letter-spacing: 2px;
  }

  .gallery-subtitle {
    text-align: center;
    font-size: 18px;
    color: #7f8c8d;
    margin-bottom: 50px;
  }

  .gallery-filters {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 50px;
    flex-wrap: wrap;
  }

  .filter-btn {
    padding: 12px 30px;
    border: 2px solid #e74c3c;
    background: transparent;
    color: #e74c3c;
    font-size: 16px;
    font-weight: bold;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .filter-btn:hover {
    background: #e74c3c;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(231, 76, 60, 0.3);
  }

  .filter-btn.active {
    background: #e74c3c;
    color: white;
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 30px;
    margin-top: 40px;
  }

  .gallery-item {
    position: relative;
    overflow: hidden;
    border-radius: 15px;
    cursor: pointer;
    aspect-ratio: 4/3;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .gallery-item:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  }

  .gallery-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  .gallery-item:hover img {
    transform: scale(1.1);
  }

  .gallery-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%);
    padding: 30px 20px 20px;
    transform: translateY(100%);
    transition: transform 0.3s ease;
  }

  .gallery-item:hover .gallery-overlay {
    transform: translateY(0);
  }

  .gallery-overlay h3 {
    color: white;
    font-size: 18px;
    margin: 0;
    font-weight: bold;
  }

  .zoom-icon {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .gallery-item:hover .zoom-icon {
    opacity: 1;
  }

  /* Lightbox */
  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .lightbox-close {
    position: absolute;
    top: 30px;
    right: 30px;
    background: transparent;
    border: none;
    color: white;
    font-size: 40px;
    cursor: pointer;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
  }

  .lightbox-close:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }

  .lightbox-content {
    max-width: 90%;
    max-height: 90%;
    text-align: center;
    animation: zoomIn 0.3s ease;
  }

  @keyframes zoomIn {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .lightbox-content img {
    max-width: 100%;
    max-height: 80vh;
    border-radius: 10px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  .lightbox-content h3 {
    color: white;
    margin-top: 20px;
    font-size: 24px;
  }

  /* Services Section */
  .services-section {
    padding: 80px 60px;
    background: #f8f9fa;
  }

  .services-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .services-title {
    text-align: center;
    font-size: 48px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 60px;
    letter-spacing: 2px;
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
    gap: 40px;
    margin-bottom: 40px;
  }

  .service-card {
    background: white;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .service-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  }

  .service-image {
    height: 350px;
    position: relative;
    overflow: hidden;
  }

  .service-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    transition: background 0.3s ease;
  }

  .service-card:hover .service-overlay {
    background: rgba(0, 0, 0, 0.5);
  }

  .service-content {
    position: absolute;
    bottom: 30px;
    right: 30px;
    text-align: right;
    z-index: 2;
  }

  .service-main-title {
    font-size: 36px;
    font-weight: bold;
    color: white;
    margin: 0;
    text-transform: uppercase;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .service-subtitle {
    font-size: 42px;
    font-weight: 900;
    color: white;
    margin: 5px 0 0 0;
    text-transform: uppercase;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .service-btn {
    flex: 1;
    padding: 15px 10px;
    color: white;
    border: none;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
  }

  .service-buttons {
    display: flex;
    gap: 0;
  }

  .service-btn-cart {
    background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
    border-bottom-left-radius: 15px;
  }

  .service-btn-cart:hover {
    background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
    transform: scale(1.02);
  }

  .service-btn-whatsapp {
    background: linear-gradient(135deg, #25D366 0%, #1ebe57 100%);
    border-bottom-right-radius: 15px;
  }

  .service-btn-whatsapp:hover {
    background: linear-gradient(135deg, #1ebe57 0%, #128c42 100%);
    transform: scale(1.02);
  }

  .service-btn:active {
    transform: scale(0.98);
  }

  /* Cart Page */
  .cart-page {
    min-height: 100vh;
    background: #f8f9fa;
    padding: 120px 60px 60px;
  }

  .cart-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .cart-header {
    text-align: center;
    margin-bottom: 50px;
  }

  .back-btn {
    background: transparent;
    border: 2px solid #2c3e50;
    color: #2c3e50;
    padding: 12px 30px;
    border-radius: 25px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 30px;
  }

  .back-btn:hover {
    background: #2c3e50;
    color: white;
  }

  .cart-title {
    font-size: 48px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 10px;
    letter-spacing: 2px;
  }

  .cart-subtitle {
    font-size: 18px;
    color: #7f8c8d;
  }

  .cart-empty {
    text-align: center;
    padding: 100px 20px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .empty-icon {
    font-size: 80px;
    margin-bottom: 20px;
  }

  .cart-empty h2 {
    font-size: 32px;
    color: #2c3e50;
    margin-bottom: 10px;
  }

  .cart-empty p {
    font-size: 18px;
    color: #7f8c8d;
    margin-bottom: 30px;
  }

  .btn-continue {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;
    padding: 15px 40px;
    border: none;
    border-radius: 30px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .btn-continue:hover {
    background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(231, 76, 60, 0.3);
  }

  .cart-content {
    display: grid;
    grid-template-columns: 1fr 450px;
    gap: 30px;
  }

  .cart-items {
    background: white;
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .cart-items-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
  }

  .cart-items-header h3 {
    font-size: 24px;
    color: #2c3e50;
  }

  .btn-clear {
    background: transparent;
    color: #e74c3c;
    border: 2px solid #e74c3c;
    padding: 8px 20px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .btn-clear:hover {
    background: #e74c3c;
    color: white;
  }

  .cart-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    margin-bottom: 15px;
    background: #f8f9fa;
    border-radius: 15px;
    transition: all 0.3s ease;
  }

  .cart-item:hover {
    transform: translateX(5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  .cart-item-info h4 {
    font-size: 18px;
    color: #2c3e50;
    margin-bottom: 5px;
  }

  .cart-item-info p {
    font-size: 14px;
    color: #7f8c8d;
  }

  .cart-item-actions {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .quantity-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    background: white;
    padding: 5px;
    border-radius: 25px;
  }

  .qty-btn {
    width: 30px;
    height: 30px;
    border: none;
    background: #f39c12;
    color: white;
    border-radius: 50%;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .qty-btn:hover {
    background: #e67e22;
    transform: scale(1.1);
  }

  .quantity {
    min-width: 30px;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
    color: #2c3e50;
  }

  .btn-remove {
    background: transparent;
    color: #e74c3c;
    border: 1px solid #e74c3c;
    padding: 8px 15px;
    border-radius: 15px;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .btn-remove:hover {
    background: #e74c3c;
    color: white;
  }

  .cart-checkout {
    position: sticky;
    top: 100px;
  }

  .cart-summary {
    background: white;
    border-radius: 20px;
    padding: 30px;
    margin-bottom: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .cart-summary h3 {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 20px;
  }

  .summary-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .summary-line span {
    color: #7f8c8d;
  }

  .summary-line strong {
    font-size: 24px;
    color: #2c3e50;
  }

  .summary-note {
    margin-top: 15px;
    font-size: 12px;
    color: #7f8c8d;
    font-style: italic;
  }

  .checkout-form {
    background: white;
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .checkout-form h3 {
    font-size: 20px;
    color: #2c3e50;
    margin-bottom: 20px;
  }

  .checkout-form .form-group {
    margin-bottom: 20px;
  }

  .checkout-form label {
    display: block;
    font-size: 14px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .checkout-form input,
  .checkout-form textarea {
    width: 100%;
    padding: 12px 15px;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  .checkout-form input:focus,
  .checkout-form textarea:focus {
    outline: none;
    border-color: #f39c12;
    box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.1);
  }

  .checkout-form textarea {
    resize: vertical;
    min-height: 80px;
  }

  .btn-checkout {
    width: 100%;
    padding: 18px;
    background: linear-gradient(135deg, #25D366 0%, #1ebe57 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 10px;
  }

  .btn-checkout:hover:not(:disabled) {
    background: linear-gradient(135deg, #1ebe57 0%, #128c42 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(37, 211, 102, 0.3);
  }

  .btn-checkout:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .checkout-success {
    margin-top: 15px;
    padding: 15px;
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
    border-radius: 10px;
    text-align: center;
    font-weight: bold;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .header {
      padding: 15px 30px;
    }

    .header.scrolled {
      padding: 12px 30px;
    }

    .logo-text {
      font-size: 18px;
    }

    .logo-circle {
      width: 40px;
      height: 40px;
      font-size: 22px;
    }

    .btn-llamanos {
      padding: 12px 25px;
      font-size: 14px;
    }

    .header-right {
      gap: 15px;
    }

    .social-icons {
      gap: 10px;
    }

    .social-icon {
      width: 35px;
      height: 35px;
    }

    .social-icon svg {
      width: 16px;
      height: 16px;
    }

    .footer {
      padding: 40px 30px 20px;
    }

    .footer-content {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .footer-social {
      justify-content: flex-start;
    }

    .hero {
      padding: 0 30px;
      margin-top: 70px;
      height: calc(100vh - 70px);
    }

    .hero-content h1 {
      font-size: 32px;
    }

    .hero-subtitle {
      font-size: 14px;
      letter-spacing: 2px;
    }

    .btn-cotizar {
      padding: 15px 35px;
      font-size: 14px;
    }

    .chat-widget {
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
    }

    .wave svg {
      height: 80px;
    }

    .services-section {
      padding: 60px 30px;
    }

    .services-title {
      font-size: 32px;
      margin-bottom: 40px;
    }

    .services-grid {
      grid-template-columns: 1fr;
      gap: 30px;
    }

    .service-image {
      height: 300px;
    }

    .service-main-title {
      font-size: 28px;
    }

    .service-subtitle {
      font-size: 32px;
    }

    .service-content {
      bottom: 20px;
      right: 20px;
    }

    .service-btn {
      padding: 18px;
      font-size: 16px;
    }

    .gallery-section {
      padding: 80px 30px;
    }

    .gallery-title {
      font-size: 36px;
    }

    .gallery-subtitle {
      font-size: 16px;
      margin-bottom: 40px;
    }

    .gallery-filters {
      gap: 15px;
      margin-bottom: 40px;
    }

    .filter-btn {
      padding: 10px 25px;
      font-size: 14px;
    }

    .gallery-grid {
      grid-template-columns: 1fr;
      gap: 25px;
    }

    .lightbox {
      padding: 20px;
    }

    .lightbox-close {
      top: 20px;
      right: 20px;
      font-size: 30px;
      width: 40px;
      height: 40px;
    }

    .lightbox-content h3 {
      font-size: 18px;
    }

    .contact-form-section {
      padding: 60px 30px;
    }

    .contact-title {
      font-size: 36px;
    }

    .contact-subtitle {
      font-size: 16px;
      margin-bottom: 40px;
    }

    .contact-form {
      padding: 30px;
    }

    .form-row {
      grid-template-columns: 1fr;
      gap: 20px;
      margin-bottom: 20px;
    }

    .contact-info-boxes {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .location-section {
      padding: 60px 30px;
    }

    .location-title {
      font-size: 36px;
    }

    .location-subtitle {
      font-size: 16px;
    }

    .map-container iframe {
      height: 300px;
    }

    .location-details {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .footer-sercotec {
      padding: 30px 30px;
    }

    .sercotec-logo img {
      max-width: 150px;
    }

    .cart-page {
      padding: 100px 30px 30px;
    }

    .cart-title {
      font-size: 36px;
    }

    .cart-content {
      grid-template-columns: 1fr;
    }

    .cart-checkout {
      position: static;
    }

    .service-btn {
      font-size: 12px;
      padding: 12px 8px;
    }

    .cart-icon-container {
      width: 40px;
      height: 40px;
    }
  }

  @media (max-width: 480px) {
    .hero-content h1 {
      font-size: 26px;
    }

    .logo-text {
      font-size: 16px;
    }

    .btn-llamanos {
      padding: 10px 20px;
      font-size: 12px;
    }

    .social-icons {
      display: none;
    }

    .footer {
      padding: 30px 20px 15px;
    }

    .footer-section h3 {
      font-size: 20px;
    }

    .footer-section h4 {
      font-size: 16px;
    }

    .services-title {
      font-size: 28px;
    }

    .service-image {
      height: 250px;
    }

    .service-main-title {
      font-size: 24px;
    }

    .service-subtitle {
      font-size: 28px;
    }

    .gallery-title {
      font-size: 28px;
    }

    .gallery-subtitle {
      font-size: 14px;
    }

    .filter-btn {
      padding: 8px 20px;
      font-size: 12px;
    }

    .gallery-grid {
      gap: 20px;
    }

    .contact-title {
      font-size: 28px;
    }

    .contact-form {
      padding: 20px;
    }

    .form-group input,
    .form-group select,
    .form-group textarea {
      padding: 12px 15px;
      font-size: 14px;
    }

    .contact-submit-btn {
      padding: 15px;
      font-size: 16px;
    }

    .location-title {
      font-size: 28px;
    }

    .map-container iframe {
      height: 250px;
    }

    .location-detail-item {
      padding: 20px;
    }

    .sercotec-logo img {
      max-width: 120px;
    }

    .sercotec-disclaimer {
      font-size: 11px;
    }

    .cart-title {
      font-size: 28px;
    }

    .service-btn {
      font-size: 11px;
      padding: 10px 5px;
      letter-spacing: 0.5px;
    }

    .cart-item {
      flex-direction: column;
      gap: 15px;
      align-items: flex-start;
    }

    .cart-item-actions {
      width: 100%;
      justify-content: space-between;
    }

    .checkout-form {
      padding: 20px;
    }

    .btn-checkout {
      font-size: 14px;
      padding: 15px;
    }
  }
`;

export default BarkerProducciones;
